import type { Entry } from 'contentful';
import type {
  IContentPageFields,
  INavigationItem,
  INavigationItemFields
} from '~~/lib/ContentfulService';
import type { IContentPage } from '~~/lib/ContentfulService/generated/contentful';
import { isDefined } from '~/utils/guards/isDefined';
import type { NavigationItemFragment } from '~~/lib/ContentfulGraphqlService';
import { isEntryOfContentType } from '~/utils/guards/isEntryOfContentType';
import { normalizeBaslerIdGroupUrn } from '~/utils/entry/normalizeBaslerIdGroup';
import type { MinifiedSecondaryNavigationItem } from '~/types/MinifiedSecondaryNavigation';

export const ENTRA_CLAIM_NAME_GROUP = 'groups';

export enum VISIBILITY_OPTIONS {
  public = 'Public',
  loggedIn = 'Logged In',
  restricted = 'Restricted'
}

export type HidableEntry =
  | Entry<IContentPageFields>
  | Entry<INavigationItemFields>
  | MinifiedSecondaryNavigationItem
  | NavigationItemFragment;

type AllowedGroupReference = {
  sys?: {
    urn?: string;
    linkType?: string;
  };
};

export function currentUserMatchesPageRestrictions(
  normedEntry: NormalizedHidableEntry
) {
  const { loggedIn, user } = useShopAuth();

  if (!loggedIn.value || normedEntry.groups?.length === 0) {
    return false;
  }
  if (
    !isDefined(user.value?.claims?.[ENTRA_CLAIM_NAME_GROUP]) ||
    (Array.isArray(user.value?.claims?.[ENTRA_CLAIM_NAME_GROUP]) &&
      user.value?.claims[ENTRA_CLAIM_NAME_GROUP].length === 0)
  ) {
    return false;
  }

  return containsEntraGroup(
    normedEntry.groups,
    user.value?.claims?.[ENTRA_CLAIM_NAME_GROUP] as string[]
  );
}

export function isVisible(
  entry?: HidableEntry | null,
  ignoreShowInNavigation = false
): boolean {
  if (!entry) {
    return false;
  }

  const { loggedIn } = useShopAuth();

  const normedEntry = normalizeHideableEntry(entry);

  if (!normedEntry.showInNavigation && !ignoreShowInNavigation) {
    return false;
  }

  if (isVisibilityPublic(normedEntry)) {
    return true;
  }

  if (isVisibilityProtected(normedEntry)) {
    return loggedIn.value;
  }

  if (isVisibilityRestricted(normedEntry)) {
    return currentUserMatchesPageRestrictions(normedEntry);
  }

  return true;
}

export function isVisibilityPublic(entry?: NormalizedHidableEntry): boolean {
  return entry?.visibility === VISIBILITY_OPTIONS.public;
}
export function isVisibilityProtected(entry?: NormalizedHidableEntry): boolean {
  return entry?.visibility === VISIBILITY_OPTIONS.loggedIn;
}
export function isVisibilityRestricted(
  entry?: NormalizedHidableEntry
): boolean {
  return entry?.visibility === VISIBILITY_OPTIONS.restricted;
}

export function containsEntraGroup(
  allowedGroups?: string[],
  claimGroups?: string[]
) {
  return (
    (allowedGroups ?? []).filter((group) =>
      (claimGroups ?? [])?.includes(group)
    )?.length >= 1
  );
}

export type NormalizedHidableEntry = {
  groups: string[];
  visibility: string;
  showInNavigation: boolean;
};

export function normalizeHideableEntry(
  entry: HidableEntry
): NormalizedHidableEntry {
  if (
    isEntryOfContentType(entry, 'navigationItem') ||
    isEntryOfContentType(entry, 'contentPage')
  ) {
    return {
      groups: (
        ((entry as INavigationItem | IContentPage).fields
          .allowedBaslerIdGroups ?? []) as {
          sys: {
            urn: string;
            linkType: string;
          };
        }[]
      )
        .filter(isDefined)
        .map((group) =>
          normalizeBaslerIdGroupUrn(group?.sys?.urn, group?.sys?.linkType)
        )
        .filter(isDefined),
      visibility:
        (entry as INavigationItem | IContentPage).fields.visibility ??
        VISIBILITY_OPTIONS.public,
      ...(() => {
        if (isEntryOfContentType(entry, 'navigationItem')) {
          return {
            showInNavigation:
              (entry as INavigationItem).fields.showInNavigation ?? true
          };
        }
        return {
          showInNavigation: true
        };
      })()
    };
  }

  if ('fields' in entry) {
    const minifiedItem = entry as MinifiedSecondaryNavigationItem;
    const minifiedAllowedGroups = (minifiedItem.fields.allowedBaslerIdGroups ??
      []) as AllowedGroupReference[];

    return {
      groups: minifiedAllowedGroups
        .map((group: AllowedGroupReference) =>
          normalizeBaslerIdGroupUrn(group?.sys?.urn, group?.sys?.linkType)
        )
        .filter(isDefined),
      visibility: minifiedItem.fields.visibility ?? VISIBILITY_OPTIONS.public,
      showInNavigation: minifiedItem.fields.showInNavigation ?? true
    };
  }

  return {
    groups:
      (entry as NavigationItemFragment).allowedBaslerIdGroups?.items
        .map((group) =>
          normalizeBaslerIdGroupUrn(group?.sys?.urn, group?.sys?.linkType)
        )
        .filter(isDefined) ?? ([] as string[]),
    visibility:
      (entry as NavigationItemFragment).visibility ?? VISIBILITY_OPTIONS.public,
    showInNavigation: (entry as NavigationItemFragment).showInNavigation ?? true
  };
}
